import XLSX from "xlsx-js-style";
import moment from "moment";
import { toast } from "../../common_components/Toast/ToastUtils";
import { NETWORK_METADATA_MAP } from "../../utils/networkConstants";
import { titleStyle, subTitleStyle, dateRangeStyle, sectionHeaderStyle, columnHeaderStyle, currentRowStyle, nameColumnStyle, summaryRowStyle, emptyStyle, text, num, } from "./exportPublisherReportingExcel";
const networkLabel = (network) => {
    const lower = String(network || "").toLowerCase();
    const meta = NETWORK_METADATA_MAP[lower.replace(/[\s_]+/g, "_")] || NETWORK_METADATA_MAP[lower];
    return meta?.name || lower.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};
const rangeText = (o) => o.startDate && o.endDate ? `${moment(o.startDate).format("MM/DD/YYYY")} - ${moment(o.endDate).format("MM/DD/YYYY")}` : "";
// Worksheet from the rows of cells (+ merged ranges and column widths)
const buildSheet = (allRows, merges, maxColumns, widths) => {
    const worksheet = {};
    allRows.forEach((row, r) => {
        row.forEach((cell, c) => {
            worksheet[XLSX.utils.encode_cell({ r, c })] = cell;
        });
    });
    worksheet["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: allRows.length - 1, c: maxColumns - 1 } });
    worksheet["!merges"] = merges;
    worksheet["!cols"] = widths.map((wch) => ({ wch }));
    worksheet["!rows"] = allRows.map((_, i) => ({ hpt: i === 0 ? 35 : i === 1 ? 26 : i === 2 ? 22 : i === 3 ? 15 : 22 }));
    return worksheet;
};
const titleBlock = (allRows, merges, maxColumns, lines) => {
    lines.forEach((line) => {
        merges.push({ s: { r: allRows.length, c: 0 }, e: { r: allRows.length, c: maxColumns - 1 } });
        const row = [];
        for (let i = 0; i < maxColumns; i++)
            row.push(text(i === 0 ? line.text : "", line.style));
        allRows.push(row);
    });
};
const saveWorkbook = (worksheet, sheetName, fileName) => {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, fileName);
};
const safeName = (s) => String(s || "").replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
// ---------------------------------------------------------------------------------------------------------------------
// Breakdown tab
// ---------------------------------------------------------------------------------------------------------------------
const BREAKDOWN_METRICS = [
    { key: "placements", label: "PLACEMENTS", kind: "count" },
    { key: "clicks", label: "CLICKS", kind: "count" },
    { key: "revenue", label: "REVENUE", kind: "currency" },
    { key: "orders", label: "ORDERS", kind: "count" },
    { key: "spend", label: "SPEND", kind: "currency" },
    { key: "roas", label: "ROAS", kind: "ratio" },
];
export const exportBreakdownExcel = (rows, summary, options) => {
    if (!rows || !rows.length) {
        toast.info("No data available to export");
        return;
    }
    try {
        const isClientView = options.view === "client";
        const entity = isClientView ? "PUBLISHER" : "CLIENT";
        const columns = [
            { label: entity, width: 30 },
            { label: "NETWORKS", width: 24 },
            ...BREAKDOWN_METRICS.map((m) => ({ label: m.label, width: m.kind === "currency" ? 18 : 14 })),
        ];
        const maxColumns = columns.length;
        const allRows = [];
        const merges = [];
        titleBlock(allRows, merges, maxColumns, [
            { text: "Publisher Reporting", style: titleStyle },
            { text: `${options.name || ""} - ${isClientView ? "Publisher" : "Client"} Breakdown`, style: subTitleStyle },
            { text: rangeText(options), style: dateRangeStyle },
        ]);
        allRows.push(Array.from({ length: maxColumns }, () => text("", emptyStyle)));
        titleBlock(allRows, merges, maxColumns, [
            { text: `${isClientView ? "Publishers" : "Clients"} (${rows.length})`, style: sectionHeaderStyle },
        ]);
        allRows.push(columns.map((c, i) => text(c.label, i === 0 ? { ...columnHeaderStyle, alignment: { horizontal: "left", vertical: "center" } } : columnHeaderStyle)));
        const pushRow = (source, label, networks, nameStyle, valueStyle) => {
            const cells = [text(label, nameStyle), text(networks, valueStyle)];
            BREAKDOWN_METRICS.forEach((m) => {
                const raw = source[m.key];
                // placements can be missing (null): shown as "-"
                cells.push(raw === null || raw === undefined ? text("-", valueStyle) : num(Number(raw), m.kind, valueStyle));
            });
            allRows.push(cells);
        };
        rows.forEach((row) => pushRow(row, row.name || "-", (row.networks || []).map(networkLabel).join(", ") || "-", nameColumnStyle, currentRowStyle));
        if (summary)
            pushRow(summary, "Total Summary", "", summaryRowStyle, summaryRowStyle);
        saveWorkbook(buildSheet(allRows, merges, maxColumns, columns.map((c) => c.width)), isClientView ? "Publisher Breakdown" : "Client Breakdown", `Publisher_Reporting_${safeName(options.name) || "Breakdown"}_${isClientView ? "Publisher" : "Client"}_Breakdown_${moment().format("YYYY-MM-DD_HH-mm")}.xlsx`);
    }
    catch (error) {
        console.error("Export failed:", error);
        toast.info("Failed to export data. Please try again.");
    }
};
// ---------------------------------------------------------------------------------------------------------------------
// Placement Details tab
// ---------------------------------------------------------------------------------------------------------------------
const TYPE_LABELS = { paid: "Paid", nonpaid: "Non-Paid" };
const rateValue = (rate) => {
    if (rate === "" || rate === null || rate === undefined)
        return "-";
    const n = Number(rate);
    if (Number.isNaN(n))
        return String(rate);
    return n > 0 ? n / 100 : "-";
};
export const exportPlacementsExcel = (placements, options) => {
    if (!placements || !placements.length) {
        toast.info("No data available to export");
        return;
    }
    try {
        const columns = [
            { label: "DATE PUBLISHED", width: 16 },
            { label: "CLIENT", width: 26 },
            { label: "PUBLICATION", width: 26 },
            { label: "TITLE", width: 36 },
            { label: "CONTACT NAME", width: 22 },
            { label: "CONTACT EMAIL", width: 28 },
            { label: "CATEGORY", width: 18 },
            { label: "TYPE", width: 12 },
            { label: "RATE", width: 10 },
            { label: "TAGS", width: 18 },
            { label: "ARTICLE LINK", width: 40 },
        ];
        const maxColumns = columns.length;
        const allRows = [];
        const merges = [];
        const range = options.startDate && options.endDate
            ? `${moment(options.startDate).format("MM/DD/YYYY")} - ${moment(options.endDate).format("MM/DD/YYYY")}`
            : "";
        titleBlock(allRows, merges, maxColumns, [
            { text: "Publisher Reporting", style: titleStyle },
            { text: `${options.subject || options.name || ""} - Placement Details`, style: subTitleStyle },
            { text: range, style: dateRangeStyle },
        ]);
        allRows.push(Array.from({ length: maxColumns }, () => text("", emptyStyle)));
        titleBlock(allRows, merges, maxColumns, [{ text: `Placements (${placements.length})`, style: sectionHeaderStyle }]);
        allRows.push(columns.map((c, i) => text(c.label, i === 0 ? { ...columnHeaderStyle, alignment: { horizontal: "left", vertical: "center" } } : columnHeaderStyle)));
        placements.forEach((p) => {
            const rate = rateValue(p.commissionRate);
            const link = text(p.articleLink || "-", currentRowStyle);
            if (p.articleLink) {
                link.l = { Target: /^https?:\/\//i.test(p.articleLink) ? p.articleLink : `https://${p.articleLink}` };
                link.s = { ...currentRowStyle, font: { ...currentRowStyle.font, color: { rgb: "026AA2" }, underline: true } };
            }
            allRows.push([
                text(p.publishedDate ? moment.utc(p.publishedDate).format("MMM D, YYYY") : "-", nameColumnStyle),
                text(p.clientName || "-", currentRowStyle),
                text(p.publication || "-", currentRowStyle),
                text(p.title || "-", currentRowStyle),
                text(p.contactName || "-", currentRowStyle),
                text(p.contactEmail || "-", currentRowStyle),
                text(p.category || "-", currentRowStyle),
                text(TYPE_LABELS[p.type] || p.type || "-", currentRowStyle),
                typeof rate === "number" ? { ...num(rate, "ratio", currentRowStyle), z: Number.isInteger(rate * 100) ? "0%" : "0.00%" } : text(rate, currentRowStyle),
                text((p.tags || []).join(", ") || "-", currentRowStyle),
                link,
            ]);
        });
        // Total row
        const total = columns.map((_, i) => text(i === 0 ? "Total Placements" : i === 1 ? String(placements.length) : "", summaryRowStyle));
        allRows.push(total);
        saveWorkbook(buildSheet(allRows, merges, maxColumns, columns.map((c) => c.width)), "Placement Details", `Publisher_Reporting_${safeName(options.subject || options.name) || "Placements"}_Placement_Details_${moment().format("YYYY-MM-DD_HH-mm")}.xlsx`);
    }
    catch (error) {
        console.error("Export failed:", error);
        toast.info("Failed to export data. Please try again.");
    }
};
